import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/api";
import { Spinner } from "@/components/common";
import DocViewer from "@/components/DocViewer";
import { FirmaGerenciaSection } from "@/components/FirmaGerenciaSection";
import { EmpresasUsuariasSection } from "@/components/EmpresasUsuariasSection";
import { CalendarDays, Users, Save, ArrowRight, Stamp, Eye, UploadCloud, AlertTriangle } from "lucide-react";

function LogoMembrete({ version }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let alive = true;
    let obj;
    api.get("/membrete/logo", { params: { version }, responseType: "blob" })
      .then((r) => { obj = window.URL.createObjectURL(r.data); if (alive) setSrc(obj); })
      .catch(() => {});
    return () => { alive = false; if (obj) window.URL.revokeObjectURL(obj); };
  }, [version]);
  return src ? <img src={src} alt="Logo del membrete" className="h-12 w-auto rounded border border-slate-100 bg-white p-1" data-testid="membrete-logo" /> : null;
}

function MembreteSection() {
  const [info, setInfo] = useState(null);
  const [form, setForm] = useState({ direccion: "", telefono: "", correo: "" });
  const [logo, setLogo] = useState(null);
  const [saving, setSaving] = useState(false);
  const [previewVersion, setPreviewVersion] = useState(null);
  const fileRef = useRef();
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = () => api.get("/membrete").then((r) => {
    setInfo(r.data);
    const a = r.data.activa || {};
    setForm({ direccion: a.direccion || "", telefono: a.telefono || "", correo: a.correo || "" });
  }).catch(() => {});
  useEffect(() => { load(); }, []);

  const guardar = async () => {
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("direccion", form.direccion);
      fd.append("telefono", form.telefono);
      fd.append("correo", form.correo);
      if (logo) fd.append("file", logo);
      const { data } = await api.post("/membrete", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Membrete actualizado a la versión ${data.version}. Las plantillas quedaron en borrador para reaprobación.`);
      setLogo(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!info) return null;
  const a = info.activa || {};
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="seccion-membrete">
      <div className="mb-1 flex items-center gap-2">
        <Stamp className="h-5 w-5 text-vino-700" />
        <h2 className="font-display text-lg font-semibold text-slate-900">Membrete institucional</h2>
      </div>
      <p className="mb-4 text-sm text-slate-500">
        Diseño oficial de los documentos generados por Roge (encabezado con logo y pie con datos de contacto).
        Cada cambio crea una nueva versión visual: las plantillas pasan a borrador y deben reaprobarse;
        los documentos ya generados o firmados no se modifican.
      </p>

      <div className="mb-4 flex flex-wrap items-center gap-4 rounded-lg border border-slate-100 bg-slate-50 p-4">
        <LogoMembrete version={a.version} />
        <div className="text-sm">
          <p className="font-semibold text-slate-800" data-testid="membrete-version-activa">Versión activa: v{a.version}</p>
          <p className="text-slate-600">{a.direccion}</p>
          <p className="text-slate-600">Teléfono: {a.telefono} · Correo: {a.correo}</p>
          <p className="text-xs text-slate-400">Creada por {a.created_by} · {a.nota}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-500">Dirección</label>
          <input value={form.direccion} onChange={(e) => set("direccion", e.target.value)} data-testid="membrete-direccion" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-vino-500" />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-500">Teléfono</label>
          <input value={form.telefono} onChange={(e) => set("telefono", e.target.value)} data-testid="membrete-telefono" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-vino-500" />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-500">Correo</label>
          <input type="email" value={form.correo} onChange={(e) => set("correo", e.target.value)} data-testid="membrete-correo" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-vino-500" />
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <button type="button" onClick={() => fileRef.current?.click()} data-testid="membrete-logo-input-btn" className="flex items-center gap-2 rounded-lg border border-dashed border-slate-300 px-3 py-2 text-sm text-slate-600 hover:border-vino-400 hover:bg-vino-50/40">
          <UploadCloud className="h-4 w-4" /> {logo ? logo.name : "Actualizar logo (opcional)"}
        </button>
        <input ref={fileRef} type="file" accept=".png,.jpg,.jpeg,.webp" className="hidden" data-testid="membrete-logo-file" onChange={(e) => setLogo(e.target.files[0])} />
        <button onClick={guardar} disabled={saving || !form.direccion || !form.telefono || !form.correo} data-testid="membrete-guardar" className="flex items-center gap-2 rounded-lg bg-vino-700 px-4 py-2 text-sm font-semibold text-white hover:bg-vino-800 disabled:opacity-50">
          {saving ? <Spinner /> : <Save className="h-4 w-4" />} Guardar nueva versión
        </button>
      </div>
      <p className="mt-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> Al guardar, todas las plantillas reciben una nueva versión visual y quedan en borrador: un administrador debe revisarlas y aprobarlas nuevamente antes de emitir documentos finales.
      </p>

      {(info.versiones || []).length > 0 && (
        <div className="mt-4">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Historial de versiones (comparar)</p>
          <div className="space-y-1.5">
            {info.versiones.map((v) => (
              <div key={v.version} className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-100 px-3 py-2 text-sm" data-testid={`membrete-version-${v.version}`}>
                <span className="font-semibold text-slate-700">v{v.version}</span>
                {v.version === a.version && <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">Activa</span>}
                <span className="text-xs text-slate-400">{v.created_by} · {(v.created_at || "").slice(0, 10)}</span>
                <button onClick={() => setPreviewVersion(v.version)} data-testid={`membrete-preview-${v.version}`} className="ml-auto flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1 text-xs font-medium text-vino-700 hover:bg-vino-50">
                  <Eye className="h-3.5 w-3.5" /> Vista previa
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {previewVersion && (
        <DocViewer
          url={`/plantillas/contrato/preview?modo=prueba&membrete_version=${previewVersion}`}
          titulo={`Membrete v${previewVersion} — prueba con datos ficticios`}
          subtitulo="Contrato de ejemplo · BORRADOR / PRUEBA"
          nombreArchivo={`membrete-v${previewVersion}-prueba.pdf`}
          onClose={() => setPreviewVersion(null)}
        />
      )}
    </div>
  );
}

export default function Configuracion() {
  const { user } = useAuth();
  const [config, setConfig] = useState(null);
  const [date, setDate] = useState("");
  const [savingDate, setSavingDate] = useState(false);

  const loadConfig = () => api.get("/config").then((r) => { setConfig(r.data); setDate(r.data.implementation_date || ""); });
  useEffect(() => { loadConfig(); }, []);

  const saveDate = async () => {
    setSavingDate(true);
    try {
      await api.put("/config", { implementation_date: date });
      toast.success("Fecha de implementación guardada");
      loadConfig();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingDate(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Configuración</h1>
        <p className="mt-1 text-sm text-slate-500">Parámetros del sistema.</p>
      </div>

      {/* Fecha implementación */}
      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="mb-1 flex items-center gap-2">
          <CalendarDays className="h-5 w-5 text-vino-700" />
          <h2 className="font-display text-lg font-semibold text-slate-900">Fecha de implementación de Roge</h2>
        </div>
        <p className="mb-4 text-sm text-slate-500">
          Se usa para advertir y rechazar registros de carga inicial cuyo ingreso sea posterior a esta fecha.
          No se inventa una fecha de corte: debe definirla HUMAN.
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500">Fecha</label>
            <input data-testid="config-fecha" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-vino-500 focus:ring-2 focus:ring-vino-500/20" />
          </div>
          <button onClick={saveDate} disabled={savingDate || !date} data-testid="config-guardar-fecha" className="flex items-center gap-2 rounded-lg bg-vino-700 px-4 py-2 text-sm font-semibold text-white hover:bg-vino-800 disabled:opacity-50">
            {savingDate ? <Spinner /> : <Save className="h-4 w-4" />} Guardar
          </button>
          {config?.implementation_date && (
            <span className="text-sm text-slate-400">Actual: <strong className="text-slate-700">{config.implementation_date}</strong></span>
          )}
        </div>
      </div>

      {/* Membrete institucional (solo administradores) */}
      {can(user, "config") && <MembreteSection />}
      {can(user, "config") && <FirmaGerenciaSection />}
      {can(user, "config") && <EmpresasUsuariasSection />}

      {/* Acceso a usuarios */}
      {can(user, "usuarios") && (
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="mb-1 flex items-center gap-2">
            <Users className="h-5 w-5 text-vino-700" />
            <h2 className="font-display text-lg font-semibold text-slate-900">Cuentas de acceso</h2>
          </div>
          <p className="mb-4 text-sm text-slate-500">
            La gestión de usuarios y roles (crear, editar, desactivar, eliminar y enviar credenciales por WhatsApp)
            ahora tiene su propio módulo.
          </p>
          <Link to="/usuarios" data-testid="link-usuarios" className="inline-flex items-center gap-2 rounded-lg bg-vino-700 px-4 py-2 text-sm font-semibold text-white hover:bg-vino-800">
            Ir a Usuarios y roles <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      )}
    </div>
  );
}
